import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [enquiryStatuses, setEnquiryStatuses] = useState({});
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [aggregateStats, setAggregateStats] = useState(null);

  useEffect(() => {
    // Check if already authenticated via sessionStorage
    const authStatus = sessionStorage.getItem('adminAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchEnquiries();
      fetchAggregateStats();
      // Load saved statuses from localStorage
      const savedStatuses = localStorage.getItem('enquiryStatuses');
      if (savedStatuses) {
        setEnquiryStatuses(JSON.parse(savedStatuses));
      }
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.status === 'success') {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuthenticated', 'true');
      }
    } catch (err) {
      setLoginError('Invalid username or password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuthenticated');
    setUsername('');
    setPassword('');
  };

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact-inquiries`);
      setEnquiries(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch enquiries. Please try again.');
      console.error('Error fetching enquiries:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAggregateStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setAggregateStats(response.data);
    } catch (err) {
      console.error('Error fetching aggregate stats:', err);
    }
  };

  const updateStatus = (id, status) => {
    const newStatuses = { ...enquiryStatuses, [id]: status };
    setEnquiryStatuses(newStatuses);
    localStorage.setItem('enquiryStatuses', JSON.stringify(newStatuses));
  };

  const getUniqueCountries = () => {
    const countries = enquiries
      .map(e => e.country)
      .filter(c => c && c.trim() !== '' && !c.startsWith('TEST'));
    return [...new Set(countries)].sort();
  };

  const parseEnquiryDetails = (message) => {
    const details = {
      company: '',
      phone: '',
      product: '',
      quantity: '',
      actualMessage: message
    };

    if (message) {
      const companyMatch = message.match(/Company:\s*([^\n]+)/);
      const phoneMatch = message.match(/Phone:\s*([^\n]+)/);
      const productMatch = message.match(/Product Interest:\s*([^\n]+)/);
      const quantityMatch = message.match(/Required Quantity:\s*([^\n]+)/);
      const messageMatch = message.match(/Message:\s*([\s\S]*)/);

      if (companyMatch) details.company = companyMatch[1].trim();
      if (phoneMatch) details.phone = phoneMatch[1].trim();
      if (productMatch) details.product = productMatch[1].trim();
      if (quantityMatch) details.quantity = quantityMatch[1].trim();
      if (messageMatch) details.actualMessage = messageMatch[1].trim();
    }

    return details;
  };

  const filteredEnquiries = enquiries
    .filter(e => !e.name.startsWith('TEST_')) // Hide test entries
    .filter(e => {
      if (filterCountry && e.country !== filterCountry) return false;
      if (filterStatus) {
        const status = enquiryStatuses[e.id] || 'pending';
        if (filterStatus !== status) return false;
      }
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          e.name.toLowerCase().includes(search) ||
          e.email.toLowerCase().includes(search) ||
          (e.country && e.country.toLowerCase().includes(search)) ||
          (e.message && e.message.toLowerCase().includes(search))
        );
      }
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'timestamp') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Country', 'Company', 'Phone', 'Product Interest', 'Quantity', 'Message', 'Status'];
    const rows = filteredEnquiries.map(e => {
      const details = parseEnquiryDetails(e.message);
      const status = enquiryStatuses[e.id] || 'pending';
      return [
        new Date(e.timestamp).toLocaleDateString(),
        e.name,
        e.email,
        e.country || '',
        details.company,
        details.phone,
        details.product,
        details.quantity,
        details.actualMessage.replace(/"/g, '""'),
        status
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `hope_international_enquiries_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'contacted': return 'status-badge contacted';
      case 'converted': return 'status-badge converted';
      case 'not-interested': return 'status-badge not-interested';
      default: return 'status-badge pending';
    }
  };

  // Login Form Component
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <div className="login-container">
          <div className="login-header">
            <img 
              src="https://customer-assets.emergentagent.com/job_hopeherbals/artifacts/a098rm1o_20251223_224120_0000.png" 
              alt="Hope International Logo" 
              className="login-logo"
            />
            <h1>Hope International</h1>
            <p>Admin Dashboard Login</p>
          </div>
          <form onSubmit={handleLogin} className="login-form">
            <div className="login-field">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                disabled={loginLoading}
                data-testid="admin-username-input"
              />
            </div>
            <div className="login-field">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                disabled={loginLoading}
                data-testid="admin-password-input"
              />
            </div>
            {loginError && <div className="login-error">{loginError}</div>}
            <button 
              type="submit" 
              className="login-button" 
              disabled={loginLoading}
              data-testid="admin-login-button"
            >
              {loginLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <a href="/" className="back-to-site-link">← Back to Website</a>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_hopeherbals/artifacts/a098rm1o_20251223_224120_0000.png" 
              alt="Hope International Logo" 
            />
            <div>
              <h1>Hope International</h1>
              <p>Admin Dashboard</p>
            </div>
          </div>
          <a href="/" className="back-to-site">← Back to Website</a>
          <button onClick={handleLogout} className="logout-button" data-testid="admin-logout-button">
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        {/* Cross-Collection Aggregate Stats */}
        {aggregateStats && (
          <div className="aggregate-stats-panel" data-testid="admin-aggregate-stats">
            <div className="aggregate-header">
              <div>
                <h2>All Submissions Overview</h2>
                <p>Live counts across all 5 inquiry collections in MongoDB</p>
              </div>
              <div className="aggregate-totals">
                <div className="agg-total-card primary">
                  <span className="agg-value">{aggregateStats.total_all_submissions}</span>
                  <span className="agg-label">Total Submissions (All Time)</span>
                </div>
                <div className="agg-total-card secondary">
                  <span className="agg-value">{aggregateStats.last_7_days?.total ?? 0}</span>
                  <span className="agg-label">Last 7 Days</span>
                </div>
              </div>
            </div>
            <div className="aggregate-breakdown">
              <div className="agg-breakdown-card" data-testid="agg-contact-inquiries">
                <span className="agg-breakdown-label">Contact Inquiries</span>
                <span className="agg-breakdown-value">{aggregateStats.by_type.contact_inquiries}</span>
                <span className="agg-breakdown-recent">+{aggregateStats.last_7_days.contact_inquiries} this week</span>
              </div>
              <div className="agg-breakdown-card" data-testid="agg-quote-requests">
                <span className="agg-breakdown-label">Quote Requests</span>
                <span className="agg-breakdown-value">{aggregateStats.by_type.quote_requests}</span>
                <span className="agg-breakdown-recent">+{aggregateStats.last_7_days.quote_requests} this week</span>
              </div>
              <div className="agg-breakdown-card" data-testid="agg-sample-requests">
                <span className="agg-breakdown-label">Sample Requests</span>
                <span className="agg-breakdown-value">{aggregateStats.by_type.sample_requests}</span>
                <span className="agg-breakdown-recent">+{aggregateStats.last_7_days.sample_requests} this week</span>
              </div>
              <div className="agg-breakdown-card" data-testid="agg-coa-requests">
                <span className="agg-breakdown-label">COA Requests</span>
                <span className="agg-breakdown-value">{aggregateStats.by_type.coa_requests}</span>
                <span className="agg-breakdown-recent">+{aggregateStats.last_7_days.coa_requests} this week</span>
              </div>
              <div className="agg-breakdown-card" data-testid="agg-farmer-partnerships">
                <span className="agg-breakdown-label">Farmer Applications</span>
                <span className="agg-breakdown-value">{aggregateStats.by_type.farmer_partnerships}</span>
                <span className="agg-breakdown-recent">+{aggregateStats.last_7_days.farmer_partnerships} this week</span>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-value">{filteredEnquiries.length}</span>
              <span className="stat-label">Total Enquiries</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon pending">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-value">
                {filteredEnquiries.filter(e => (enquiryStatuses[e.id] || 'pending') === 'pending').length}
              </span>
              <span className="stat-label">Pending</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon contacted">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-value">
                {filteredEnquiries.filter(e => enquiryStatuses[e.id] === 'contacted').length}
              </span>
              <span className="stat-label">Contacted</span>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon converted">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="stat-info">
              <span className="stat-value">
                {filteredEnquiries.filter(e => enquiryStatuses[e.id] === 'converted').length}
              </span>
              <span className="stat-label">Converted</span>
            </div>
          </div>
        </div>

        {/* Filters & Actions */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="search-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
              <option value="">All Countries</option>
              {getUniqueCountries().map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="not-interested">Not Interested</option>
            </select>
            
            <select value={`${sortField}-${sortOrder}`} onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortField(field);
              setSortOrder(order);
            }}>
              <option value="timestamp-desc">Newest First</option>
              <option value="timestamp-asc">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="country-asc">Country A-Z</option>
            </select>
          </div>
          
          <div className="actions-row">
            <button className="btn-refresh" onClick={fetchEnquiries}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Refresh
            </button>
            <button className="btn-export" onClick={exportToCSV}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>

        {/* Enquiries Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading enquiries...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={fetchEnquiries}>Try Again</button>
            </div>
          ) : filteredEnquiries.length === 0 ? (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p>No enquiries found</p>
            </div>
          ) : (
            <table className="enquiries-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Contact</th>
                  <th>Company</th>
                  <th>Country</th>
                  <th>Product Interest</th>
                  <th>Quantity</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEnquiries.map((enquiry) => {
                  const details = parseEnquiryDetails(enquiry.message);
                  const status = enquiryStatuses[enquiry.id] || 'pending';
                  
                  return (
                    <tr key={enquiry.id}>
                      <td className="date-cell">
                        {formatDate(enquiry.timestamp)}
                      </td>
                      <td className="contact-cell">
                        <strong>{enquiry.name}</strong>
                        <a href={`mailto:${enquiry.email}`}>{enquiry.email}</a>
                        {details.phone && <span className="phone">{details.phone}</span>}
                      </td>
                      <td>{details.company || '-'}</td>
                      <td>{enquiry.country || '-'}</td>
                      <td className="product-cell">{details.product || '-'}</td>
                      <td>{details.quantity || '-'}</td>
                      <td className="message-cell">
                        {details.actualMessage ? (
                          <span className="message-preview">
                            {details.actualMessage.substring(0, 50)}
                            {details.actualMessage.length > 50 ? '...' : ''}
                          </span>
                        ) : '-'}
                      </td>
                      <td>
                        <span className={getStatusBadgeClass(status)}>
                          {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button 
                          className="btn-view"
                          onClick={() => setSelectedEnquiry(enquiry)}
                          title="View Details"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                        <select 
                          value={status}
                          onChange={(e) => updateStatus(enquiry.id, e.target.value)}
                          className="status-select"
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="not-interested">Not Interested</option>
                        </select>
                        <a 
                          href={`mailto:${enquiry.email}?subject=Re: Your Inquiry to Hope International&body=Dear ${enquiry.name},%0D%0A%0D%0AThank you for your interest in Hope International.%0D%0A%0D%0A`}
                          className="btn-email"
                          title="Send Email"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                          </svg>
                        </a>
                        {details.phone && (
                          <a 
                            href={`https://wa.me/${details.phone.replace(/[^0-9]/g, '')}`}
                            className="btn-whatsapp"
                            target="_blank"
                            rel="noopener noreferrer"
                            title="WhatsApp"
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Enquiry Details Modal */}
      {selectedEnquiry && (
        <div className="modal-overlay" onClick={() => setSelectedEnquiry(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedEnquiry(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            
            <div className="modal-header">
              <h2>Enquiry Details</h2>
              <span className={getStatusBadgeClass(enquiryStatuses[selectedEnquiry.id] || 'pending')}>
                {(enquiryStatuses[selectedEnquiry.id] || 'pending').charAt(0).toUpperCase() + (enquiryStatuses[selectedEnquiry.id] || 'pending').slice(1).replace('-', ' ')}
              </span>
            </div>
            
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Date & Time</label>
                  <p>{formatDate(selectedEnquiry.timestamp)}</p>
                </div>
                <div className="detail-item">
                  <label>Name</label>
                  <p>{selectedEnquiry.name}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p><a href={`mailto:${selectedEnquiry.email}`}>{selectedEnquiry.email}</a></p>
                </div>
                <div className="detail-item">
                  <label>Country</label>
                  <p>{selectedEnquiry.country || '-'}</p>
                </div>
                {(() => {
                  const details = parseEnquiryDetails(selectedEnquiry.message);
                  return (
                    <>
                      {details.company && (
                        <div className="detail-item">
                          <label>Company</label>
                          <p>{details.company}</p>
                        </div>
                      )}
                      {details.phone && (
                        <div className="detail-item">
                          <label>Phone / WhatsApp</label>
                          <p><a href={`tel:${details.phone}`}>{details.phone}</a></p>
                        </div>
                      )}
                      {details.product && (
                        <div className="detail-item">
                          <label>Product Interest</label>
                          <p>{details.product}</p>
                        </div>
                      )}
                      {details.quantity && (
                        <div className="detail-item">
                          <label>Required Quantity</label>
                          <p>{details.quantity}</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              
              <div className="message-section">
                <label>Message / Additional Requirements</label>
                <div className="message-content">
                  {(() => {
                    const details = parseEnquiryDetails(selectedEnquiry.message);
                    return details.actualMessage || selectedEnquiry.message || 'No message provided';
                  })()}
                </div>
              </div>
              
              <div className="modal-actions">
                <select 
                  value={enquiryStatuses[selectedEnquiry.id] || 'pending'}
                  onChange={(e) => updateStatus(selectedEnquiry.id, e.target.value)}
                  className="status-select-large"
                >
                  <option value="pending">Mark as Pending</option>
                  <option value="contacted">Mark as Contacted</option>
                  <option value="converted">Mark as Converted</option>
                  <option value="not-interested">Mark as Not Interested</option>
                </select>
                <a 
                  href={`mailto:${selectedEnquiry.email}?subject=Re: Your Inquiry to Hope International&body=Dear ${selectedEnquiry.name},%0D%0A%0D%0AThank you for your interest in Hope International's herbal products.%0D%0A%0D%0AWe have received your inquiry and would like to discuss your requirements further.%0D%0A%0D%0ABest regards,%0D%0AHope International Team`}
                  className="btn-reply"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Reply via Email
                </a>
                {(() => {
                  const details = parseEnquiryDetails(selectedEnquiry.message);
                  if (details.phone) {
                    return (
                      <a 
                        href={`https://wa.me/${details.phone.replace(/[^0-9]/g, '')}?text=Hello ${selectedEnquiry.name}, Thank you for your interest in Hope International. We have received your inquiry and would like to discuss further.`}
                        className="btn-whatsapp-large"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp
                      </a>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
